import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { connexionParBiometrie, marquerActive, messageErreurBiometrie } from '../services/biometrie.service';

// Configuration d'axios
const API_URL = process.env.REACT_APP_API_URL || '/api';

// Créer une instance axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Contexte d'authentification
const AuthContext = createContext({});

// Hook pour utiliser le contexte
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

// Provider d'authentification
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Fonction pour mettre à jour l'utilisateur
  const updateUser = useCallback((updatedData) => {
    setUser(prevUser => {
      const newUser = { ...prevUser, ...updatedData };
      localStorage.setItem('user', JSON.stringify(newUser));
      return newUser;
    });
  }, []);

  // Configurer l'intercepteur axios pour ajouter le token
  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.data?.requireRelogin) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, [navigate, enqueueSnackbar]);

  // Vérifier le token au chargement
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await api.get('/auth/me');
          setUser(response.data.data.membre);
        }
      } catch (error) {
        console.error('Erreur de vérification du token:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Fonction de connexion
  const login = useCallback(async (telephone, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/auth/login', {
        telephone,
        password
      });

      const { token, membre } = response.data.data;
      
      // Sauvegarder le token et l'utilisateur
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(membre));
      setUser(membre);
      
      enqueueSnackbar('Connexion réussie !', { variant: 'success' });

      // Rediriger selon le rôle — tous les rôles du bureau vont vers l'espace admin
      if (['administrateur', 'president', 'tresorier', 'tresorier_adjoint', 'secretaire_general', 'secretaire_general_adjoint', 'commissaire'].includes(membre.role)) {
        navigate('/admin/dashboard');
      } else {
        navigate('/member/dashboard');
      }
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur de connexion';
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [navigate, enqueueSnackbar]);

  // Connexion par empreinte / visage (le téléphone vérifie, le serveur contrôle la signature)
  const loginBiometrique = useCallback(async () => {
    try {
      // Pas de setLoading global ici : il vide la page de connexion pendant que le telephone
      // demande l'empreinte. Le bouton affiche lui-meme « Verification en cours ».
      setError(null);

      const { token, membre } = await connexionParBiometrie(api);

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(membre));
      setUser(membre);
      marquerActive(membre.id);

      enqueueSnackbar('Connexion réussie !', { variant: 'success' });

      if (['administrateur', 'president', 'tresorier', 'tresorier_adjoint', 'secretaire_general', 'secretaire_general_adjoint', 'commissaire'].includes(membre.role)) {
        navigate('/admin/dashboard');
      } else {
        navigate('/member/dashboard');
      }

      return { success: true };
    } catch (error) {
      const message = messageErreurBiometrie(error);
      setError(message);
      enqueueSnackbar(message, { variant: ['NotAllowedError', 'AbortError'].includes(error?.name) ? 'info' : 'error' });
      return { success: false, error: message };
    }
  }, [navigate, enqueueSnackbar]);

  // Fonction d'inscription
  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/auth/register', userData);
      
      const { token, membre } = response.data.data;
      
      // Vérifier le statut du compte
      if (membre.statut === 'en_attente') {
        // Compte en attente de validation : NE PAS connecter
        enqueueSnackbar(
          'Inscription réussie ! Votre compte est en attente de validation par un administrateur. Vous recevrez une notification par WhatsApp.',
          { 
            variant: 'info',
            autoHideDuration: 8000  // 8 secondes pour lire le message
          }
        );
        
        // Rediriger vers le blog (pas de connexion)
        navigate('/blog');
        
        return { 
          success: true, 
          membre,
          enAttente: true  // Indicateur pour le composant RegisterPage
        };
      } else {
        // Compte actif (premier admin) : connecter automatiquement
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(membre));
        setUser(membre);
        
        enqueueSnackbar('Inscription réussie !', { variant: 'success' });
        
        // Rediriger selon le rôle — tous les rôles du bureau vont vers l'espace admin
        if (['administrateur', 'president', 'tresorier', 'tresorier_adjoint', 'secretaire_general', 'secretaire_general_adjoint', 'commissaire'].includes(membre.role)) {
          navigate('/admin/dashboard');
        } else {
          navigate('/member/dashboard');
        }
        
        return { 
          success: true, 
          membre,
          enAttente: false
        };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de l\'inscription';
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [navigate, enqueueSnackbar]);

  // Fonction de déconnexion
  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      // Nettoyer le stockage local et l'état
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      enqueueSnackbar('Déconnexion réussie', { variant: 'info' });
      navigate('/');
    }
  }, [navigate, enqueueSnackbar]);

  // Fonction pour mettre à jour le profil
  const updateProfile = useCallback(async (data) => {
    try {
      setLoading(true);
      const response = await api.put(`/membres/${user.id}`, data);
      const updatedUser = response.data.data;
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      enqueueSnackbar('Profil mis à jour avec succès', { variant: 'success' });
      return { success: true, data: updatedUser };
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de la mise à jour du profil';
      enqueueSnackbar(message, { variant: 'error' });
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [user, enqueueSnackbar]);

  // Fonction pour changer le mot de passe
  const changePassword = useCallback(async (oldPassword, newPassword) => {
    try {
      setLoading(true);
      await api.post('/auth/change-password', {
        oldPassword,
        newPassword
      });
      enqueueSnackbar('Mot de passe modifié avec succès', { variant: 'success' });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors du changement de mot de passe';
      enqueueSnackbar(message, { variant: 'error' });
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Vérifier les permissions
  const hasRole = useCallback((roles) => {
    if (!user) return false;
    if (typeof roles === 'string') {
      return user.role === roles;
    }
    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }
    return false;
  }, [user]);

  const isAdmin = useCallback(() => {
    return hasRole(['administrateur', 'president', 'tresorier', 'tresorier_adjoint', 'secretaire_general', 'secretaire_general_adjoint', 'commissaire']);
  }, [hasRole]);

  const isSuperAdmin = useCallback(() => {
    return hasRole(['administrateur', 'president']);
  }, [hasRole]);

  // Valeur du contexte
  const value = {
    user,
    loading,
    error,
    login,
    loginBiometrique,
    logout,
    register,
    updateUser,
    updateProfile,
    changePassword,
    hasRole,
    isAdmin,
    isSuperAdmin,
    api, // Exposer l'instance axios configurée
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
